'use client';

import { useEffect } from 'react';

import { TThemeColorScheme } from '@northladder/services';
import { loadLocalData } from '@northladder/utilities';

import { APP_THEME_STORE_STORAGE_KEY, IThemeType } from './types';
import { useThemeStore } from './useThemeStore';

/**
 * -----------------------------------------------------------------------------
 * This controls the theme switching and color palettes. It injects the JS
 * script that preloads the current theme and color palette into the dom to
 * prevent the flash of the previous or defaults.
 */
export function ThemeColorPaletteScript({
  colorScheme: initColorScheme,
}: IThemeType) {
  const storeKey = APP_THEME_STORE_STORAGE_KEY;

  const cachedThemeStore = loadLocalData<IThemeType>(storeKey);
  const colorScheme = cachedThemeStore?.colorScheme || initColorScheme;
  const { setColorScheme } = useThemeStore();

  const updateDom = (scheme: TThemeColorScheme) => {
    const options = [
      'green-scheme',
      'purple-scheme',
      'red-scheme',
      'zinc-scheme',
      'amber-scheme',
    ];

    if (options.includes(scheme)) {
      document.documentElement.classList.remove(...options);
      document.documentElement.classList.add(scheme);
    }
  };

  useEffect(() => {
    setColorScheme(colorScheme);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorScheme]);

  return (
    <script
      /* eslint-disable react/no-danger */
      dangerouslySetInnerHTML={{
        __html: `(${updateDom.toString()})("${colorScheme}")`,
      }}
      suppressHydrationWarning
    />
  );
}
